import AddIcon from '@mui/icons-material/Add'
import QueuePlayNext from '@mui/icons-material/QueuePlayNext'
import RemoveIcon from '@mui/icons-material/Remove'
import { Box, Fab, FormControl, FormHelperText, Grid } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form'
import Select from 'react-select'

import { FormTextField } from '../../../base/components/Form'
import { MyInputLabel } from '../../../base/components/MyInputs/MyInputLabel'
import { reactSelectStyle } from '../../../base/components/MySelect/ReactSelect'
import NumberSpinnerField from '../../../base/components/NumberSpinnerField/NumberSpinnerField'
import SimpleCard from '../../../base/components/Template/Cards/SimpleCard'
import { handleSelect } from '../../../utils/helper'
import { ArticuloUnidadMedidaProp } from '../../unidadMedida/interfaces'
import { apiArticuloUnidadMedidaListado } from '../api/apiArticuloUniMed'
import { GrupoUnidadMedidaInputProps } from '../interfaces'

interface OwnProps {
  form: UseFormReturn<GrupoUnidadMedidaInputProps>
  handleEdit: boolean
}

type Props = OwnProps

const GrupoUnidadMedidaForm: FunctionComponent<Props> = ({ form, handleEdit }) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = form

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'definicion',
  })

  const { data: unidadesMedida = [] } = useQuery<ArticuloUnidadMedidaProp[], Error>({
    queryKey: ['unidadesMedida'],
    queryFn: apiArticuloUnidadMedidaListado,
  })
  const baseUnit = watch('codigoUnidadMedidaBase')

  const initializedRef = React.useRef(false)

  useEffect(() => {
    if (!initializedRef.current && fields.length === 0) {
      append({ codigoUnidadMedida: '', cantidadBase: 0 })
      initializedRef.current = true
    }
  }, [fields.length, append])

  const selectedUnidades = useMemo(() => {
    const units = watch('definicion')?.map((item) => item.codigoUnidadMedida) || []
    return units.filter(Boolean)
  }, [watch])

  const filteredBaseOptions = useMemo(
    () =>
      unidadesMedida.filter(
        (unidad) => !selectedUnidades.includes(unidad.codigoUnidadMedida),
      ),
    [unidadesMedida, selectedUnidades],
  )

  const getFilteredOptions = (currentIndex: number) => {
    const currentValue = watch(`definicion.${currentIndex}.codigoUnidadMedida`)
    const allValues = watch('definicion') || []

    const selectedUnidades = allValues
      .map((item, index) => (index !== currentIndex ? item.codigoUnidadMedida : null))
      .filter((val) => val !== null)

    return unidadesMedida.filter(
      (unidad) =>
        unidad.codigoUnidadMedida === currentValue ||
        (!selectedUnidades.includes(unidad.codigoUnidadMedida) &&
          unidad.codigoUnidadMedida !== baseUnit),
    )
  }

  const handleAddRow = () => {
    append({ codigoUnidadMedida: '', cantidadBase: 0 })
  }

  return (
    <Box>
      <SimpleCard
        title={'Unidad de Medida - Grupo'}
        childIcon={<QueuePlayNext sx={{ mr: 0.5, color: 'primary.main' }} />}
      >
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="nombreGrupo"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormTextField
                  {...field}
                  label="Nombre Grupo"
                  size="small"
                  fullWidth
                  error={!!error}
                  helperText={error?.message?.toString()}
                  margin="dense"
                  slotProps={{ inputLabel: { shrink: true } }}
                  placeholder="Ingrese nombre del grupo de unidad de medida"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              control={control}
              name="codigoUnidadMedidaBase"
              rules={{ required: 'Debe seleccionar una unidad de medida base' }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  error={!!errors.codigoUnidadMedidaBase}
                  size="small"
                  margin="dense"
                >
                  <MyInputLabel shrink>Unidad Medida Primaria</MyInputLabel>
                  <Select
                    placeholder="Seleccione una unidad de medida base"
                    styles={reactSelectStyle(!!errors.codigoUnidadMedidaBase)}
                    menuPosition={'fixed'}
                    value={
                      unidadesMedida.find(
                        (unidad) => unidad.codigoUnidadMedida === field.value,
                      ) || null
                    }
                    onChange={(unidadMedida) => {
                      if (!handleEdit) {
                        field.onChange(unidadMedida?.codigoUnidadMedida || '')
                      }
                    }}
                    options={filteredBaseOptions}
                    getOptionValue={(item) => item.codigoUnidadMedida.toString()}
                    getOptionLabel={(item) =>
                      `${item.codigoUnidadMedida} - ${item.nombreUnidadMedida}`
                    }
                  />
                  <FormHelperText>
                    {errors.codigoUnidadMedidaBase?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </SimpleCard>
      <SimpleCard
        title={'Definiciones de unidad de medida equivalentes a la unidad base'}
        childIcon={<QueuePlayNext sx={{ mr: 0.5, color: 'primary.main' }} />}
        sx={{ mt: 2 }}
      >
        {fields.map((field, index) => (
          <Grid container spacing={1} alignItems="center" key={field.id}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <Controller
                name={`definicion.${index}.codigoUnidadMedida`}
                control={control}
                rules={{
                  required: 'Debe seleccionar una unidad de medida equivalente',
                }}
                render={({ field: fieldProps }) => (
                  <FormControl
                    fullWidth
                    error={!!errors?.definicion?.[index]?.codigoUnidadMedida}
                    size="small"
                    margin="dense"
                  >
                    <MyInputLabel shrink>Unidad de Medida</MyInputLabel>
                    <Select
                      {...fieldProps}
                      placeholder="Seleccione unidad de medida"
                      styles={reactSelectStyle(
                        !!errors?.definicion?.[index]?.codigoUnidadMedida,
                      )}
                      menuPosition={'fixed'}
                      value={
                        fieldProps.value
                          ? unidadesMedida.find(
                              (unidad) => unidad.codigoUnidadMedida === fieldProps.value,
                            )
                          : null
                      }
                      onChange={(selected) => {
                        fieldProps.onChange(selected?.codigoUnidadMedida || '')
                      }}
                      options={getFilteredOptions(index)}
                      getOptionValue={(item) => item.codigoUnidadMedida.toString()}
                      getOptionLabel={(item) =>
                        `${item.codigoUnidadMedida} - ${item.nombreUnidadMedida}`
                      }
                    />
                    <FormHelperText>
                      {errors?.definicion?.[index]?.codigoUnidadMedida?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <FormControl
                fullWidth
                error={!!errors?.definicion?.[index]?.cantidadBase}
                size="small"
                margin="dense"
              >
                <NumberSpinnerField
                  id={`cantidad-${index}`}
                  label="Cantidad"
                  fullWidth
                  value={watch(`definicion.${index}.cantidadBase`)}
                  onClick={handleSelect}
                  min={0}
                  step={1}
                  decimalScale={2}
                  onChange={(cantidad) => {
                    setValue(`definicion.${index}.cantidadBase`, cantidad ?? 0, {
                      shouldValidate: true,
                    })
                  }}
                  spinnerTabIndex={false}
                  sx={{ backgroundColor: 'white' }}
                />
                <FormHelperText>
                  {errors?.definicion?.[index]?.cantidadBase?.message}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 2 }} display="flex" justifyContent="flex-end">
              {fields.length > 1 && (
                <Fab
                  color="error"
                  aria-label="remove"
                  onClick={() => remove(index)}
                  size="small"
                  sx={{ mr: 0.5 }}
                >
                  <RemoveIcon />
                </Fab>
              )}
              {index === fields.length - 1 && (
                <Fab color="primary" aria-label="add" onClick={handleAddRow} size="small">
                  <AddIcon />
                </Fab>
              )}
            </Grid>
          </Grid>
        ))}
      </SimpleCard>
    </Box>
  )
}

export default GrupoUnidadMedidaForm
